import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { loginRedirectGuard } from './core/guards/login-redirect.guard';
import { requireProductAccess, requireNoProductAccess } from './core/guards/product-access.guard';

const buildAccess = [requireProductAccess('build', '/product-disabled')];
const buildDisabled = [requireNoProductAccess('build', '/dashboard')];

export const routes: Routes = [
  { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
  {
    // Guard-only: the in-app login UI was removed under centralized auth. The
    // guard exchanges an OAuth ?code callback that may land on /login or hands
    // off to the centralized portal. No component renders here.
    path: 'login',
    canActivate: [loginRedirectGuard],
    children: [],
  },
  {
    // CLI device-code authorization. Reachable from the URL `crewdle login --sso`
    // prints; users sign in inline (any Firebase Auth provider) and approve.
    path: 'cli-device',
    loadComponent: () => import('./features/cli-device/cli-device.component'),
  },
  // Authenticated pages (inside layout with sidebar)
  {
    path: '',
    loadComponent: () => import('./layout/layout.component'),
    canActivate: [authGuard],
    children: [
      {
        path: 'product-disabled',
        loadComponent: () => import('./features/product-disabled/product-disabled.component'),
        canActivate: buildDisabled,
      },
      {
        path: 'dashboard',
        loadComponent: () => import('./features/dashboard/dashboard.component'),
        canActivate: buildAccess,
      },
      {
        path: 'account',
        loadComponent: () => import('./features/account/account.component'),
      },
      {
        path: 'environments',
        loadComponent: () => import('./features/environments/environments.component'),
        canActivate: buildAccess,
      },
      {
        // Brand Kits — org-shared design systems (environments pattern:
        // create once, many projects attach), maintained by a UX/UI Designer agent.
        path: 'brand-kits',
        loadComponent: () => import('./features/brand-kits/brand-kits.component'),
        canActivate: buildAccess,
      },
      {
        path: 'insights',
        loadChildren: () =>
          import('./features/insights/insights.routes').then((m) => m.INSIGHTS_ROUTES),
        canActivate: buildAccess,
      },
    ],
  },
  {
    // Brand Kit detail — full-screen workspace chrome (like project/:id):
    // toolbar on top, Designer chat left, the kit artifact right. Lives
    // OUTSIDE the sidebar layout on purpose.
    path: 'brand-kits/:id',
    loadComponent: () => import('./features/brand-kits/brand-kit-detail.component'),
    canActivate: [authGuard, ...buildAccess],
  },
  {
    // One environment's card, read-only and chrome-less — embedded by Crewdle
    // Connect's preview pane (Vincent, 2026-10-01). Outside the sidebar layout.
    path: 'environments/:id/view',
    loadComponent: () => import('./features/environments/environments.component'),
    canActivate: [authGuard, ...buildAccess],
    data: { viewOnly: true },
  },
  {
    path: 'project/:id',
    loadComponent: () => import('./features/workspace/workspace.component'),
    canActivate: [authGuard, ...buildAccess],
    children: [
      { path: '', redirectTo: 'code', pathMatch: 'full'},
      {
        path: 'plan',
        loadComponent: () =>
          import('./features/workspace/modules/plan/plan.component'),
      },
      {
        path: 'preview',
        loadComponent: () =>
          import('./features/workspace/modules/prototype/prototype.component'),
      },
      {
        path: 'design-system',
        loadComponent: () =>
          import('./features/workspace/modules/design-system/design-system.component'),
      },
      {
        path: 'journey-map',
        loadComponent: () =>
          import('./features/workspace/modules/journey-map/journey-map.component'),
      },
      {
        path: 'mental-model',
        loadComponent: () =>
          import('./features/workspace/modules/mental-model/mental-model.component'),
      },
      {
        path: 'task-model',
        loadComponent: () =>
          import('./features/workspace/modules/task-model/task-model.component'),
      },
      {
        path: 'assets',
        loadComponent: () =>
          import('./features/workspace/modules/workflows/workflows.component'),
      },
      { path: 'workflows', redirectTo: 'assets', pathMatch: 'full' },
      {
        path: 'code',
        loadComponent: () =>
          import('./features/workspace/modules/code/code.component'),
      },
      {
        // Test Plans — QA-owned test suites + execution reports (QA agent).
        path: 'test-plans',
        loadComponent: () =>
          import('./features/workspace/modules/test-plans/test-plans.component'),
      },
    ],
  },
  // Share pages (public, no auth/product gate)
  {
    // Brand-kit client share: Storybook + comments (buildGetSharedKit).
    path: 'share-kit/:token',
    loadComponent: () => import('./features/share/share-kit-view.component'),
  },
  {
    path: 'share/:token',
    loadComponent: () => import('./features/share/share-view.component'),
    children: [
      { path: '', redirectTo: 'preview', pathMatch: 'full'},
      {
        path: 'preview',
        loadComponent: () =>
          import('./features/workspace/modules/prototype/prototype.component'),
      },
      {
        path: 'design-system',
        loadComponent: () =>
          import('./features/workspace/modules/design-system/design-system.component'),
      },
      {
        path: 'journey-map',
        loadComponent: () =>
          import('./features/workspace/modules/journey-map/journey-map.component'),
      },
      {
        path: 'mental-model',
        loadComponent: () =>
          import('./features/workspace/modules/mental-model/mental-model.component'),
      },
      {
        path: 'task-model',
        loadComponent: () =>
          import('./features/workspace/modules/task-model/task-model.component'),
      },
    ],
  },
];
